import { useEffect, useRef, useState } from "react";
import { View, useWindowDimensions } from "react-native";
import {
  Compass,
  Snowflake,
  Gem,
  HeartHandshake,
  HelpCircle,
  CalendarClock,
  Stethoscope,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenTitle,
  ScreenSubtitle,
} from "../components/OnboardingLayout";
import GlassChoiceButton from "../components/GlassChoiceButton";
import { AUTO_ADVANCE_DELAY } from "../theme";
import { useTranslations } from "@src/packages/i18n";
import { getOnboardingViewport } from "../onboardingViewport";
import type { FertilityIntent } from "@src/packages/fertility-intent";
import type { OnboardingStackParamList, TreatmentIntent } from "../types";
import { routeAfterJourneySelection } from "../onboardingFlow";

type Props = NativeStackScreenProps<OnboardingStackParamList, "JourneyStage">;

type StageOption = {
  labelKey: string;
  sublabelKey?: string;
  intent: TreatmentIntent;
  fertilityStage?: FertilityIntent["stage"];
  Icon: typeof Compass;
};

const PRESERVE_OPTIONS: StageOption[] = [
  {
    labelKey: "onboarding.journey_stage.exploring_label",
    sublabelKey: "onboarding.journey_stage.exploring_sublabel",
    intent: "exploring",
    Icon: Compass,
  },
  {
    labelKey: "onboarding.journey_stage.egg_freezing_label",
    sublabelKey: "onboarding.journey_stage.egg_freezing_sublabel",
    intent: "egg-freezing",
    Icon: Snowflake,
  },
  {
    labelKey: "onboarding.journey_stage.sperm_freezing_label",
    sublabelKey: "onboarding.journey_stage.sperm_freezing_sublabel",
    intent: "sperm-preserve",
    Icon: Snowflake,
  },
  {
    labelKey: "onboarding.journey_stage.embryo_freezing_label",
    sublabelKey: "onboarding.journey_stage.embryo_freezing_sublabel",
    intent: "embryo-preserve",
    Icon: Gem,
  },
];

const GROW_OPTIONS: StageOption[] = [
  {
    labelKey: "onboarding.journey_stage.trying_naturally_label",
    sublabelKey: "onboarding.journey_stage.trying_naturally_sublabel",
    intent: "pregnancy",
    fertilityStage: "trying-naturally",
    Icon: HeartHandshake,
  },
  {
    labelKey: "onboarding.journey_stage.exploring_options_label",
    sublabelKey: "onboarding.journey_stage.exploring_options_sublabel",
    intent: "exploring",
    fertilityStage: "exploring-options",
    Icon: HelpCircle,
  },
  {
    labelKey: "onboarding.journey_stage.getting_ready_label",
    sublabelKey: "onboarding.journey_stage.getting_ready_sublabel",
    intent: "pregnancy",
    fertilityStage: "getting-ready",
    Icon: CalendarClock,
  },
  {
    labelKey: "onboarding.journey_stage.in_treatment_label",
    sublabelKey: "onboarding.journey_stage.in_treatment_sublabel",
    intent: "pregnancy",
    fertilityStage: "in-treatment",
    Icon: Stethoscope,
  },
];

export default function JourneyStageScreen({ navigation, route }: Props) {
  const { width, height } = useWindowDimensions();
  const { condensed } = getOnboardingViewport(width, height);
  const compactChoices = condensed || width < 430;
  const t = useTranslations();
  const { stage } = route.params;
  const { data, updateField } = useOnboarding();
  // Partner context and donor/carrier support are independent axes. Keep the
  // complete stage set here and defer donor/carrier details until Home rather
  // than inferring a treatment path from the partner answer.
  const subOptions = stage === "preserve" ? PRESERVE_OPTIONS : GROW_OPTIONS;
  const [selectedKey, setSelectedKey] = useState<string | null>(
    () =>
      subOptions.find(
        (option) =>
          option.intent === data.treatmentIntent &&
          (option.fertilityStage ?? null) === (data.fertilityStage ?? null),
      )?.labelKey ?? null,
  );
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advancing = useRef(false);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const subTitle =
    stage === "preserve"
      ? t("onboarding.journey_stage.preserve_title")
      : t("onboarding.journey_stage.grow_title");
  const subDescription =
    stage === "preserve"
      ? t("onboarding.journey_stage.preserve_subtitle")
      : t("onboarding.journey_stage.grow_subtitle");

  const handleSelect = (opt: StageOption) => {
    if (advancing.current) return;
    advancing.current = true;
    setSelectedKey(opt.labelKey);
    updateField("treatmentIntent", opt.intent);
    updateField("fertilityStage", opt.fertilityStage ?? null);
    updateField("plannedTreatmentModality", null);
    updateField("currentTreatmentFamily", null);

    if (stage === "preserve") {
      updateField(
        "gender",
        opt.intent === "egg-freezing"
          ? "AFAB"
          : opt.intent === "sperm-preserve"
            ? "AMAB"
            : null,
      );
    }

    advanceTimer.current = setTimeout(() => {
      advancing.current = false;
      advanceTimer.current = null;
      const nextRoute = routeAfterJourneySelection(
        stage,
        opt.intent,
        opt.fertilityStage ?? null,
      );
      if (nextRoute.name === "TreatmentType") {
        navigation.navigate(nextRoute.name, nextRoute.params);
      } else {
        navigation.navigate(nextRoute.name);
      }
    }, AUTO_ADVANCE_DELAY);
  };

  return (
    <OnboardingLayout>
      <ScreenTitle>{subTitle}</ScreenTitle>
      <ScreenSubtitle>{subDescription}</ScreenSubtitle>

      <View style={{ gap: compactChoices ? 10 : 14 }}>
        {subOptions.map((opt) => (
          <GlassChoiceButton
            key={opt.labelKey}
            Icon={opt.Icon}
            label={t(opt.labelKey)}
            sublabel={opt.sublabelKey ? t(opt.sublabelKey) : undefined}
            onPress={() => handleSelect(opt)}
            compact={compactChoices}
            selected={selectedKey === opt.labelKey}
          />
        ))}
      </View>
    </OnboardingLayout>
  );
}
